import { TASK_REGISTRY } from '../constants/taskRegistry';
import { isRouteGroupDocumentWorkflowEnabled } from '../features/routeGroups/routeGroupFeatureFlags';
import type {
  WorkspaceApplication,
  WorkspaceTask,
  WorkspaceTransportType,
} from '../store/api/workspaceApi';

export type ScopedTaskPresentationMode = 'legacy' | 'scoped';

export type ScopedTaskPresentation = {
  mode: ScopedTaskPresentationMode;
  lines: string[];
  titleLine: string;
  tooltip: string;
  legacyPrimaryLine: string;
  legacySecondaryLine: string;
};

export type ScopedTaskPresentationContext = {
  applications?: WorkspaceApplication[];
  clientShortNameByGroupId?: Map<number, string>;
  clientShortNameByApplicationId?: Map<number, string>;
  legacyEntityLabel?: string | null;
};

function nonEmptyLine(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function formatScopedTransportTypeLabel(
  transportType: WorkspaceTransportType | null | undefined
): string | null {
  if (transportType == null) return null;
  const value = String(transportType).trim().toUpperCase();
  if (value === 'AUTO' || value === 'RAIL' || value === 'AIR') return value;
  return null;
}

export function resolveTaskTypeLabel(taskType: string | null | undefined): string {
  const tt = String(taskType || '').toUpperCase();
  return TASK_REGISTRY[tt]?.label ?? (tt || 'Задача');
}

function clientDisplayFromApplication(app: WorkspaceApplication | undefined): string | null {
  if (!app?.client) return null;
  return nonEmptyLine(app.client.shortName) ?? nonEmptyLine(app.client.name);
}

function applicationNumberFromGroupLabel(groupLabel: string | null | undefined): string | null {
  const label = nonEmptyLine(groupLabel);
  if (!label) return null;
  const match = label.match(/№\s*([^\s(]+)/);
  return match?.[1]?.trim() ?? null;
}

export function resolveClientShortNameForScopedTask(
  task: WorkspaceTask,
  context?: ScopedTaskPresentationContext
): string | null {
  if (task.groupId != null && context?.clientShortNameByGroupId?.has(task.groupId)) {
    return context.clientShortNameByGroupId.get(task.groupId) ?? null;
  }
  if (task.applicationId != null && context?.clientShortNameByApplicationId?.has(task.applicationId)) {
    return context.clientShortNameByApplicationId.get(task.applicationId) ?? null;
  }

  const applications = context?.applications ?? [];
  if (task.applicationId != null) {
    const fromAppId = clientDisplayFromApplication(applications.find((a) => a.id === task.applicationId));
    if (fromAppId) return fromAppId;
  }

  const appNumber = applicationNumberFromGroupLabel(task.groupLabel);
  if (appNumber && applications.length > 0) {
    const matched = applications.find(
      (app) =>
        nonEmptyLine(app.internalNumber) === appNumber || nonEmptyLine(app.number) === appNumber
    );
    const fromGroupLabel = clientDisplayFromApplication(matched);
    if (fromGroupLabel) return fromGroupLabel;
  }

  return nonEmptyLine(task.applicationClientName);
}

export function buildScopedGroupDisplayLine(
  task: WorkspaceTask,
  context?: ScopedTaskPresentationContext
): string | null {
  const base = nonEmptyLine(task.groupLabel) ?? nonEmptyLine(context?.legacyEntityLabel);
  const client = resolveClientShortNameForScopedTask(task, context);
  if (!base && !client) return null;
  if (!base) return client;
  if (!client) return base;
  if (base.includes(`(${client})`)) return base;
  return `${base} (${client})`;
}

export function buildLegacyEntityLine(task: WorkspaceTask): string {
  const hasRouteSnapshot = task.routeId != null;
  if (hasRouteSnapshot) {
    return `${task.loadingCity ?? '—'} → ${task.unloadingCity ?? '—'} · ${task.carrierName?.trim() || '—'} · #${task.routeId}`;
  }
  if (task.entityType === 'APPLICATION' && task.applicationId != null) {
    return `Заявка #${task.applicationId}`;
  }
  return task.routeId != null ? `Маршрут #${task.routeId}` : '—';
}

export function buildScopedTaskPresentation(
  task: WorkspaceTask,
  options?: {
    scopedEnabled?: boolean;
    context?: ScopedTaskPresentationContext;
    titleOverride?: string | null;
    legacyEntityLabel?: string | null;
  }
): ScopedTaskPresentation {
  const titleLine = nonEmptyLine(options?.titleOverride) ?? resolveTaskTypeLabel(task.taskType);
  const legacyPrimaryLine = titleLine;
  const legacySecondaryLine = buildLegacyEntityLine(task);
  const scopedEnabled = options?.scopedEnabled ?? isRouteGroupDocumentWorkflowEnabled();
  const context: ScopedTaskPresentationContext = {
    ...options?.context,
    legacyEntityLabel: options?.legacyEntityLabel ?? options?.context?.legacyEntityLabel ?? null,
  };

  if (!scopedEnabled) {
    return {
      mode: 'legacy',
      lines: [legacyPrimaryLine, legacySecondaryLine],
      titleLine,
      tooltip: `${legacyPrimaryLine}\n${legacySecondaryLine}`,
      legacyPrimaryLine,
      legacySecondaryLine,
    };
  }

  const taskScope = String(task.taskScope || 'ROUTE').toUpperCase();
  if (taskScope === 'ROUTE') {
    return {
      mode: 'legacy',
      lines: [legacyPrimaryLine, legacySecondaryLine],
      titleLine,
      tooltip: `${legacyPrimaryLine}\n${legacySecondaryLine}`,
      legacyPrimaryLine,
      legacySecondaryLine,
    };
  }

  const groupLine = buildScopedGroupDisplayLine(task, context);
  const transportLine = formatScopedTransportTypeLabel(task.transportType);

  if (taskScope === 'GROUP') {
    const lines = [transportLine, groupLine, titleLine].filter((line): line is string => line != null);
    return {
      mode: 'scoped',
      lines,
      titleLine,
      tooltip: lines.join('\n'),
      legacyPrimaryLine,
      legacySecondaryLine,
    };
  }

  if (taskScope === 'STOP') {
    const stopLine = nonEmptyLine(task.stopLabel);
    const lines = [transportLine, groupLine, stopLine, titleLine].filter(
      (line): line is string => line != null
    );
    return {
      mode: 'scoped',
      lines,
      titleLine,
      tooltip: lines.join('\n'),
      legacyPrimaryLine,
      legacySecondaryLine,
    };
  }

  return {
    mode: 'legacy',
    lines: [legacyPrimaryLine, legacySecondaryLine],
    titleLine,
    tooltip: `${legacyPrimaryLine}\n${legacySecondaryLine}`,
    legacyPrimaryLine,
    legacySecondaryLine,
  };
}

export function buildClientShortNameLookupFromRouteApplications(
  applications: Array<Record<string, unknown>> | null | undefined,
  applicationGroups: Array<{ id: number; applicationId?: number | null }> | null | undefined
): Map<number, string> {
  const byAppId = new Map<number, string>();
  for (const link of applications ?? []) {
    const app = (link.application ?? null) as Record<string, unknown> | null;
    const client = (app?.client ?? null) as Record<string, unknown> | null;
    const applicationId = Number(link.applicationId ?? app?.id ?? 0);
    if (!Number.isFinite(applicationId) || applicationId <= 0) continue;
    const shortName =
      client?.shortName != null ? String(client.shortName).trim() : '';
    const name = client?.name != null ? String(client.name).trim() : '';
    const display = shortName || name;
    if (display) byAppId.set(applicationId, display);
  }

  const byGroupId = new Map<number, string>();
  for (const group of applicationGroups ?? []) {
    const applicationId = group.applicationId;
    if (applicationId == null) continue;
    const client = byAppId.get(applicationId);
    if (client) byGroupId.set(group.id, client);
  }
  return byGroupId;
}
